import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import {
  CreateDriverSiteDistributionInput,
  GqlService,
  updateDriverSiteDistributionMutation,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import { SiteDistributionStore } from '../site-distribution-store.service';

@Injectable({ providedIn: 'root' })
export class SiteDistributionService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private siteDistributionStore = inject(SiteDistributionStore);

  async createSiteDistribution(site: CreateDriverSiteDistributionInput) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createDriverSiteDistribution$(site)
    );

    if (success && data) {
      this.siteDistributionStore.upsert(data.id, {
        ...data,
      });
    } else {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }

  getSiteCurveDistributions(id?: string) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.siteDistributionStore.setLoading(true);

        return this.gqlService.listDriverSiteDistributions$([], id);
      }),
      tap(({ data, errors, success }) => {
        if (success && data) {
          this.siteDistributionStore.set(data);
        } else {
          this.overlayService.error(errors);
        }

        this.siteDistributionStore.setLoading(false);
      })
    );
  }

  async updateSiteDistribution(x: updateDriverSiteDistributionMutation) {
    this.siteDistributionStore.setLoading(true);
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updateDriverSiteDistribution$({
        distribution_mode: x.distribution_mode,
        distribution_month: x.distribution_month,
        id: x.id,
        net_sites_per_month: x.net_sites_per_month,
        net_sites_per_month_percentage: x.net_sites_per_month_percentage,
        sites_activated: x.sites_activated,
        sites_activated_percentage: x.sites_activated_percentage,
        sites_closed: x.sites_closed,
        sites_closed_percentage: x.sites_closed_percentage,
      })
    );

    if (success && data) {
      this.siteDistributionStore.update(data.id, {
        distribution_mode: data.distribution_mode,
        distribution_month: data.distribution_month,
        id: data.id,
        net_sites_per_month: data.net_sites_per_month,
        net_sites_per_month_percentage: data.net_sites_per_month_percentage,
        sites_activated: data.sites_activated,
        sites_activated_percentage: data.sites_activated_percentage,
        sites_closed: data.sites_closed,
        sites_closed_percentage: data.sites_closed_percentage,
      });
      this.siteDistributionStore.setLoading(false);

      return true;
    }

    this.overlayService.error(errors);

    this.siteDistributionStore.setLoading(false);

    return false;
  }
}
